
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {FiltrationOfNullable} from './FiltrationOfNullable';
import {FieldModelFiltrationOfNullable} from './FiltrationOfNullable';

/**
 * FilterString struct
 */
class FilterString {
  /**
   * Initialize struct
   * @param {!string=} contains
   * @param {!Set=} exactValues
   * @param {!boolean=} isCaseSensitive
   * @param {FiltrationOfNullable=} filtrationOfNullable
   * @constructor
   */
  constructor (contains = '', exactValues = new Set(), isCaseSensitive = false, filtrationOfNullable = null) {
    this.Contains = contains
    this.ExactValues = exactValues
    this.IsCaseSensitive = isCaseSensitive
    this.FiltrationOfNullable = filtrationOfNullable
  }

  /**
   * Copy struct (shallow copy)
   * @this {!FilterString}
   * @param {!FilterString} other Other struct
   * @returns {!FilterString} This struct
   */
  copy (other) {
    if (other.Contains != null) {
      this.Contains = other.Contains
    } else {
      this.Contains = null
    }
    if (other.ExactValues != null) {
      this.ExactValues = new Set()
      for (let item of other.ExactValues) {
        if (item != null) {
          let tempItem
          tempItem = item
          this.ExactValues.add(tempItem)
        } else {
          this.ExactValues.add(null)
        }
      }
    } else {
      this.ExactValues = null
    }
    if (other.IsCaseSensitive != null) {
      this.IsCaseSensitive = other.IsCaseSensitive
    } else {
      this.IsCaseSensitive = null
    }
    if (other.FiltrationOfNullable != null) {
      this.FiltrationOfNullable = FiltrationOfNullable.fromObject(other.FiltrationOfNullable)
    } else {
      this.FiltrationOfNullable = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!FilterString}
   * @returns {!FilterString} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new FilterStringModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new FilterStringModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!FilterString}
   * @param {!FilterString} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof FilterString)) {
      throw new TypeError('Instance of FilterString is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!FilterString}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Contains: ((this.Contains != null) ? this.Contains : null),
      ExactValues: ((this.ExactValues != null) ? Array.from(this.ExactValues, item => ((item != null) ? item : null)) : null),
      IsCaseSensitive: ((this.IsCaseSensitive != null) ? this.IsCaseSensitive : null),
      FiltrationOfNullable: ((this.FiltrationOfNullable != null) ? this.FiltrationOfNullable : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return FilterString.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!FilterString} other Object value
   * @returns {!FilterString} Created struct
   */
  static fromObject (other) {
    return new FilterString().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!FilterString}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return FilterString.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!FilterString}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 111
  }
}

export { FilterString };

/**
 * Fast Binary Encoding FilterString field model
 */
class FieldModelFilterString extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Contains = new fbe.FieldModelString(buffer, 4 + 4)
    this.ExactValues = new fbe.FieldModelSet(new fbe.FieldModelString(buffer, this.Contains.fbeOffset + this.Contains.fbeSize), buffer, this.Contains.fbeOffset + this.Contains.fbeSize)
    this.IsCaseSensitive = new fbe.FieldModelBool(buffer, this.ExactValues.fbeOffset + this.ExactValues.fbeSize)
    this.FiltrationOfNullable = new fbe.FieldModelOptional(new FieldModelFiltrationOfNullable(buffer, this.IsCaseSensitive.fbeOffset + this.IsCaseSensitive.fbeSize), buffer, this.IsCaseSensitive.fbeOffset + this.IsCaseSensitive.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelFilterString}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelFilterString}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Contains.fbeSize + this.ExactValues.fbeSize + this.IsCaseSensitive.fbeSize + this.FiltrationOfNullable.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelFilterString}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Contains.fbeExtra + this.ExactValues.fbeExtra + this.IsCaseSensitive.fbeExtra + this.FiltrationOfNullable.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelFilterString}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelFilterString.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelFilterString}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 111
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelFilterString}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelFilterString}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Contains.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Contains.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Contains.fbeSize

    if ((fbeCurrentSize + this.ExactValues.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ExactValues.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ExactValues.fbeSize

    if ((fbeCurrentSize + this.IsCaseSensitive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IsCaseSensitive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsCaseSensitive.fbeSize

    if ((fbeCurrentSize + this.FiltrationOfNullable.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.FiltrationOfNullable.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FiltrationOfNullable.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelFilterString}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelFilterString}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelFilterString}
   * @param {!FilterString} fbeValue Default value, defaults is new FilterString()
   * @returns {!FilterString} FilterString value
   */
  get (fbeValue = new FilterString()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelFilterString}
   * @param {!FilterString} fbeValue FilterString value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Contains.fbeSize) <= fbeStructSize) {
      fbeValue.Contains = this.Contains.get()
    } else {
      fbeValue.Contains = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Contains.fbeSize

    if ((fbeCurrentSize + this.ExactValues.fbeSize) <= fbeStructSize) {
      this.ExactValues.get(fbeValue.ExactValues)
    } else {
      fbeValue.ExactValues.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ExactValues.fbeSize

    if ((fbeCurrentSize + this.IsCaseSensitive.fbeSize) <= fbeStructSize) {
      fbeValue.IsCaseSensitive = this.IsCaseSensitive.get(false)
    } else {
      fbeValue.IsCaseSensitive = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsCaseSensitive.fbeSize

    if ((fbeCurrentSize + this.FiltrationOfNullable.fbeSize) <= fbeStructSize) {
      fbeValue.FiltrationOfNullable = this.FiltrationOfNullable.get()
    } else {
      fbeValue.FiltrationOfNullable = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FiltrationOfNullable.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelFilterString}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelFilterString}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelFilterString}
   * @param {!FilterString} fbeValue FilterString value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelFilterString}
   * @param {FilterString} fbeValue FilterString value
   */
  setFields (fbeValue) {
    this.Contains.set(fbeValue.Contains)
    this.ExactValues.set(fbeValue.ExactValues)
    this.IsCaseSensitive.set(fbeValue.IsCaseSensitive)
    this.FiltrationOfNullable.set(fbeValue.FiltrationOfNullable)
  }
}

export { FieldModelFilterString };

/**
 * Fast Binary Encoding FilterString model
 */
class FilterStringModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelFilterString(this.buffer, 4)
  }

  /**
   * Get the FilterString model
   * @this {!FilterStringModel}
   * @returns {!FieldModelFilterString} model FilterString model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!FilterStringModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!FilterStringModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return FilterStringModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!FilterStringModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelFilterString.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!FilterStringModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!FilterStringModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!FilterStringModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!FilterStringModel}
   * @param {!FilterString} value FilterString value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!FilterStringModel}
   * @param {!FilterString} value FilterString value, defaults is new FilterString()
   * @return {!object} Deserialized FilterString value and its size
   */
  deserialize (value = new FilterString()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new FilterString(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new FilterString(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!FilterStringModel}
   * @param {!number} prev Previous FilterString model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { FilterStringModel };
